import { resourcesWithTags } from "./resourcesData";

const parseDateAdded = (dateString) => {
  if (!dateString) return null;
  const parts = dateString.split("-").map(Number);
  if (parts.length !== 3) return null;
  const [day, month, year] = parts;
  return new Date(year, month - 1, day);
};

const isNew = (dateString) => {
  const addedDate = parseDateAdded(dateString);
  if (!addedDate) return false;
  const now = new Date();
  const diffInDays = (now - addedDate) / (1000 * 60 * 60 * 24);
  return diffInDays <= 14;
};


const normalizeTag = (tag) => {
  const trimmed = tag.trim();
  if (!trimmed) return "";
  const words = trimmed.split(/\s+/);
  return words
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
};

const normalizeText = (value) =>
  String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();

// Expand "NEW" if resource < 7 days old and attach parsed date
const expandedResources = resourcesWithTags.map((resource) => {
  const normalizedTags = resource.tags.map(normalizeTag);
  if (isNew(resource.dateAdded)) {
    normalizedTags.unshift("NEW");
  }
  const parsedDate = parseDateAdded(resource.dateAdded);
  return { ...resource, tags: normalizedTags, parsedDate };
});

export function useResources(searchTerm, selectedTags) {
  // Filter by searchTerm (title or reference)
  const filteredBySearch = (() => {
    if (!searchTerm.trim()) return expandedResources;
    const normalizedSearch = normalizeText(searchTerm);

    return expandedResources.filter((res) => {
      const fields = [
        res.title,
        res.reference,
        res.dateAdded,
        ...(res.tags || []),
      ];

      return fields.some((field) => normalizeText(field).includes(normalizedSearch));
    });
  })();

  const filteredByTags = filteredBySearch.filter((res) => {
    if (selectedTags.length === 0) return true;
    return selectedTags.every((tag) => res.tags.includes(tag));
  });

  // Sort by parsedDate (newest first)
  const sortedResources = (() => {
    const sorted = [...filteredByTags];
    sorted.sort((a, b) => {
      const da = a.parsedDate ? a.parsedDate.getTime() : 0;
      const db = b.parsedDate ? b.parsedDate.getTime() : 0;
      return db - da; // newest first
    });
    return sorted;
  })();

  // Unique tags ordered by popularity (frequency) across expandedResources
  const uniqueTags = (() => {
    const counts = new Map();
    expandedResources.forEach((res) => {
      res.tags.forEach((tag) => counts.set(tag, (counts.get(tag) || 0) + 1));
    });

    const arrayTags = Array.from(counts.keys()).sort((a, b) => {
      const ca = counts.get(a) || 0;
      const cb = counts.get(b) || 0;
      if (ca !== cb) return cb - ca; // descending by count
      return a.localeCompare(b); // tie-breaker: alphabetical
    });

    // Move "NEW" to the front if present
    if (arrayTags.includes("NEW")) {
      const filtered = arrayTags.filter((t) => t !== "NEW");
      return ["NEW", ...filtered];
    }
    return arrayTags;
  })();

  const activeTags = (() => {
    const active = new Set();
    sortedResources.forEach((resource) => {
      resource.tags.forEach((tag) => active.add(tag));
    });
    return Array.from(active);
  })();

  return {
    resources: sortedResources,
    tags: uniqueTags,
    activeTags,
    total: expandedResources.length,
  };
}
